import React, { useState, useEffect, useRef } from "react";
import {
  Upload,
  Image as ImageIcon,
  Trash2,
  ExternalLink,
  Plus,
  Loader2,
  Check,
  AlertCircle,
} from "lucide-react";
import { projectsService } from "@/api/projectsService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

/**
 * ============================================================
 * PROJECT MEDIA MANAGER (SUPABASE STORAGE `project-media`)
 * ============================================================
 * Direct asset uploader and media management component.
 * Strictly uses Supabase Storage bucket `project-media`.
 * Never relies on Cloudinary for project assets.
 */
export default function ProjectMediaManager({ projectId }) {
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // New media modal/form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [mediaType, setMediaType] = useState("gallery");
  const [altText, setAltText] = useState("");
  const [caption, setCaption] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);

  const loadMedia = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const items = await projectsService.getProjectMedia(projectId);
      setMediaList(items);
    } catch (err) {
      console.error("[ProjectMediaManager] Error loading media:", err);
      toast.error("Could not load project media assets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      loadMedia();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/svg+xml"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file format. Please upload JPEG, PNG, WebP, or SVG.");
      return;
    }

    // Validate size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File is too large. Max allowed size is 10MB.");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    if (!altText) {
      // Default alt text to clean file name
      setAltText(file.name.replace(/\.[^/.]+$/, "").replace(/[-_]+/g, " "));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Please select an image to upload.");
      return;
    }
    if (!projectId) {
      toast.error("Save the project first before uploading media.");
      return;
    }

    try {
      setUploading(true);
      await projectsService.uploadProjectMedia(selectedFile, projectId, {
        mediaType,
        altText,
        caption,
        displayOrder,
      });

      toast.success("Media asset uploaded to Supabase Storage.");
      // Reset form
      setSelectedFile(null);
      setPreviewUrl("");
      setAltText("");
      setCaption("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadMedia();
    } catch (err) {
      console.error("[ProjectMediaManager] Upload error:", err);
      toast.error(err.message || "Failed to upload media asset.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaItem) => {
    if (!window.confirm("Are you sure you want to delete this media asset?")) return;
    try {
      await projectsService.deleteProjectMedia(mediaItem.id, mediaItem.storage_path);
      toast.success("Media asset deleted.");
      setMediaList((prev) => prev.filter((m) => m.id !== mediaItem.id));
    } catch (err) {
      console.error("[ProjectMediaManager] Delete error:", err);
      toast.error(err.message || "Failed to delete media asset.");
    }
  };

  if (!projectId) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-xs text-slate-500 shadow-xs">
        <ImageIcon className="w-8 h-8 text-slate-300 mx-auto mb-3" />
        <p className="font-semibold text-slate-800">Project Must Be Saved First</p>
        <p className="mt-1 font-medium">Please create the project before uploading media assets to Supabase Storage.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Box */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4 shadow-xs">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Upload className="w-4 h-4 text-cyan-600" />
              <span>Upload Project Asset</span>
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
              Images are stored securely in Supabase Storage (`project-media`) with RLS policy enforcement.
            </p>
          </div>
          <Badge className="bg-cyan-50 text-cyan-700 border-cyan-200 font-mono text-[10px]">
            Supabase Storage
          </Badge>
        </div>

        <form onSubmit={handleUpload} className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* File Selector Drop Area */}
            <div>
              <Label className="text-xs font-semibold text-slate-700 block mb-1.5">Image File (JPG, PNG, WebP, SVG)</Label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 hover:border-cyan-400 rounded-xl p-5 text-center cursor-pointer transition-colors bg-slate-50/50 hover:bg-slate-50 flex flex-col items-center justify-center min-h-[140px]"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {previewUrl ? (
                  <div className="relative group">
                    <img
                      src={previewUrl}
                      alt="Upload Preview"
                      className="max-h-24 rounded-lg object-contain border border-slate-200 shadow-xs"
                    />
                    <div className="text-[10px] text-cyan-600 mt-2 font-semibold">Click to change file</div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-6 h-6 text-slate-400 mb-2" />
                    <span className="text-xs text-slate-700 font-medium">Choose image or drag here</span>
                    <span className="text-[10px] text-slate-400 mt-0.5">Up to 10MB per file</span>
                  </>
                )}
              </div>
            </div>

            {/* Asset Metadata */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-slate-700 block mb-1">Media Role</Label>
                <select
                  value={mediaType}
                  onChange={(e) => setMediaType(e.target.value)}
                  className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-xs text-slate-900 font-medium focus:outline-none focus:border-cyan-500"
                >
                  <option value="thumbnail" className="bg-white text-slate-900">
                    Thumbnail (Card & Listing Preview)
                  </option>
                  <option value="hero" className="bg-white text-slate-900">
                    Hero Banner (Top Header Image)
                  </option>
                  <option value="gallery" className="bg-white text-slate-900">
                    Gallery Screenshot (Case Study View)
                  </option>
                  <option value="other" className="bg-white text-slate-900">
                    Other Supporting Asset
                  </option>
                </select>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700 block mb-1">Alt Text (Accessibility & SEO)</Label>
                <Input
                  type="text"
                  placeholder="e.g. EduMagik student analytics dashboard"
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 block mb-1">Caption (Optional)</Label>
                  <Input
                    type="text"
                    placeholder="Short caption"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 rounded-xl focus:bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 block mb-1">Display Order</Label>
                  <Input
                    type="number"
                    value={displayOrder}
                    onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
                    className="h-10 bg-slate-50 border-slate-200 text-xs text-slate-900 rounded-xl focus:bg-white"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={!selectedFile || uploading}
              className="h-9 px-5 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-semibold gap-2 shadow-sm"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Uploading to Storage...</span>
                </>
              ) : (
                <>
                  <Upload className="w-3.5 h-3.5" />
                  <span>Upload Asset</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Existing Media Gallery */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Uploaded Media ({mediaList.length})
          </h4>
        </div>

        {loading ? (
          <div className="py-8 text-center text-xs text-slate-400 font-medium">Loading project media...</div>
        ) : mediaList.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500 font-medium">
            No media assets uploaded yet. Use the uploader above to add thumbnails, banners, or screenshots.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {mediaList.map((item) => (
              <div
                key={item.id}
                className="group relative rounded-xl border border-slate-200 bg-white overflow-hidden hover:border-cyan-300 transition-colors flex flex-col shadow-xs"
              >
                <div className="aspect-video bg-slate-100 relative overflow-hidden flex items-center justify-center">
                  <img
                    src={item.publicUrl}
                    alt={item.alt_text || "Project Asset"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <Badge
                    className="absolute top-2 left-2 text-[10px] uppercase font-semibold tracking-wider bg-white/90 backdrop-blur-sm border-slate-200 text-slate-800 shadow-xs"
                  >
                    {item.media_type}
                  </Badge>
                </div>

                <div className="p-3 flex-1 flex flex-col justify-between">
                  <div>
                    <div className="text-xs font-semibold text-slate-900 truncate" title={item.alt_text}>
                      {item.alt_text || "Untitled"}
                    </div>
                    {item.caption && (
                      <div className="text-[10px] text-slate-500 truncate mt-0.5 font-medium" title={item.caption}>
                        {item.caption}
                      </div>
                    )}
                  </div>

                  <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                    <a
                      href={item.publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-cyan-600 hover:underline inline-flex items-center gap-1 font-semibold"
                    >
                      <span>View Full</span>
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>

                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item)}
                      className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50 rounded-lg"
                      title="Delete asset"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
